/** biome-ignore-all lint/security/noDangerouslySetInnerHtml: <explanation> */

import type {
	ContactCardUmbBlockElementModel,
	ContactCardUmbBlockSettingsElementModel,
	IApiMediaWithCropsModel,
} from "#/_generated/umbraco/delivery-api/models";
import { getUmbracoCmsBaseUrlEnv } from "#/_packages/umbrella/config/utils.ts";
import {
	ContactCard,
	type ContactCardDetail,
	type ContactCardDetailType,
	type ContactCardOrientation,
} from "#/_packages/umbrella/features/contact-card/components/contact-card";
import type { BlockItemProps } from "#/_packages/umbrella/integrations/umbraco/types";
import {
	tryGetAlignment,
	tryGetAnimationScrollType,
	tryGetBoxVariant,
} from "#/_packages/umbrella/utils";
import { getAltText } from "#/_packages/umbrella/utils/image-utils.ts";

type ContactCardBlockProps = BlockItemProps & {
	content: ContactCardUmbBlockElementModel;
	settings: ContactCardUmbBlockSettingsElementModel;
};

const knownDetailTypes: ContactCardDetailType[] = [
	"name",
	"role",
	"jobTitle",
	"email",
	"phone",
	"mobile",
	"website",
	"company",
	"department",
	"other",
];

function parseDetails(
	details: string[] | null | undefined,
): ContactCardDetail[] {
	if (!details) return [];
	return details.map((detail) => {
		const separatorIndex = detail.indexOf("|");
		if (separatorIndex > 0) {
			const type = detail.substring(0, separatorIndex) as ContactCardDetailType;
			const value = detail.substring(separatorIndex + 1);
			if (knownDetailTypes.includes(type)) {
				return { type, value };
			}
		}
		return { type: "other" as ContactCardDetailType, value: detail };
	});
}

function getContactCardImageUrl(
	image: IApiMediaWithCropsModel,
	format?: "webp" | "jpg",
) {
	const url = new URL(image.url, getUmbracoCmsBaseUrlEnv());

	url.searchParams.set("width", "480");
	url.searchParams.set("quality", "80");
	if (format) url.searchParams.set("format", format);

	return url.toString();
}

export default function ContactCardUmbBlock({
	className,
	content,
	settings,
}: ContactCardBlockProps) {
	const settingsProperties = settings.properties;
	const image = content.properties?.image?.[0];
	const details = parseDetails(content.properties?.details);
	const links = content.properties?.links;
	const isSvg =
		image?.extension?.replace(".", "").toLowerCase() === "svg" ||
		image?.url.toLowerCase().endsWith(".svg");

	return (
		<ContactCard
			id={settingsProperties?.generatedId ?? content.id}
			className={className}
			image={
				image
					? {
							src: isSvg
								? new URL(image.url, getUmbracoCmsBaseUrlEnv()).toString()
								: getContactCardImageUrl(image),
							webpSrc: isSvg
								? undefined
								: getContactCardImageUrl(image, "webp"),
							jpegSrc: isSvg ? undefined : getContactCardImageUrl(image, "jpg"),
							alt: getAltText(image),
							isSvg,
						}
					: undefined
			}
			details={details}
			description={
				content.properties?.description?.markup ? (
					<div
						className="rich-text"
						dangerouslySetInnerHTML={{
							__html: content.properties.description.markup,
						}}
					/>
				) : undefined
			}
			links={links}
			orientation={
				(content.properties?.orientation as ContactCardOrientation) ?? undefined
			}
			contentAlignment={tryGetAlignment(settingsProperties?.contentAlignment)}
			boxVariant={tryGetBoxVariant(settingsProperties?.boxVariant)}
			fullHeight={settingsProperties?.fullHeight ?? false}
			animationHoverType={settingsProperties?.animationHoverType ?? []}
			animationScrollType={tryGetAnimationScrollType(
				settingsProperties?.animationScrollType,
			)}
		/>
	);
}
